import type { FC } from "react";
import type React from "react";

import type { GridConfig } from "@ui/components/PushBlock";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import classNames from "classnames";

import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { Column, Row } from "@jti/core-components/components/Row";
import type { ColumnProps } from "@jti/core-components/components/Row/components/Column";

import styles from "./PushBlockContentWrapper.module.scss";

export type MinimumHeightProps = "normalHeight" | "miniHeight";
export type ImageFitProps = "cover" | "contain";
export type PushBlockContentWrapperProps = {
  className?: string;
  imgDesktop: string;
  imgMobile?: string;
  alt?: string;
  orientation: "left-side-text" | "right-side-text" | "centered" | "left-side-text-wide" | "right-side-text-wide";
  mobileOrientation?: "top-text" | "bottom-text";
  smallHeightOnMobile?: boolean;
  renderImage?: (props: Pick<PushBlockContentWrapperProps, "imgDesktop" | "imgMobile" | "alt">) => JSX.Element | null;
  isCarousel?: boolean;
  isFullWidthContent?: boolean;
  isBackgroundOverlayEnabled?: boolean;
  isBackgroundOverlayEnabledOnMobile?: boolean;
  renderContent?: () => React.ReactNode;
  blockHeight?: MinimumHeightProps;
  theme?: "dark" | "light" | "inherit";
  imageFit?: ImageFitProps;
  gridConfig?: GridConfig;
};

export const PushBlockContentWrapper: FC<PushBlockContentWrapperProps> = ({
  className,
  imgMobile,
  imgDesktop,
  alt = "",
  orientation,
  smallHeightOnMobile = false,
  renderImage,
  mobileOrientation = "bottom-text",
  isCarousel = false,
  isFullWidthContent = false,
  isBackgroundOverlayEnabled = false,
  isBackgroundOverlayEnabledOnMobile = false,
  blockHeight = false,
  renderContent = () => null,
  theme = "inherit",
  imageFit = "cover",
  gridConfig,
}) => {
  const { specificBreakpoint } = useCurrentBreakpoint();
  const isMobile = ["xSmall", "small", "smallMedium"].includes(specificBreakpoint);
  const isLarge = ["large", "largeMedium", "xLarge"].includes(specificBreakpoint);
  const isMedium = ["smallLarge", "medium", "mediumWide"].includes(specificBreakpoint);

  const tabletColumnWidth = gridConfig?.columnTabletWidth ?? gridConfig?.columnDesktopWidth;
  const shouldForceMobileLayout = gridConfig
    ? (isMedium && tabletColumnWidth !== "12") || (isLarge && gridConfig.columnDesktopWidth !== "12")
    : false;

  const shouldUseMobileImage = (isMobile || shouldForceMobileLayout) && imgMobile;
  const imageSrc = shouldUseMobileImage ? imgMobile : imgDesktop;

  const isCentered = orientation === "centered";
  const shouldShowFullWidthImage = isCentered || isFullWidthContent || (isMobile && smallHeightOnMobile);

  const columnMdSizeByOrientation: Partial<Record<PushBlockContentWrapperProps["orientation"], ColumnProps["md"]>> = {
    centered: 12,
    "left-side-text-wide": 6,
    "right-side-text-wide": 6,
  };
  const columnMdSize: ColumnProps["md"] =
    isCarousel || !!isFullWidthContent ? 12 : columnMdSizeByOrientation[orientation] || 4;

  const containerClasses = classNames(styles.container, styles[`${blockHeight}`], {
    [styles.imageFirst]: ["right-side-text", "right-side-text-wide"].includes(orientation),
    [styles.centered]: isCentered,
    [styles.smallHeightOnMobile]: smallHeightOnMobile,
    [styles.fullWidthContent]: !!isFullWidthContent,
    [styles.backgroundOverlayEnabled]: !!isBackgroundOverlayEnabled,
    [styles.backgroundOverlayEnabledOnMobile]: !!isBackgroundOverlayEnabledOnMobile,
    [styles.forceMobileLayout]: shouldForceMobileLayout,
    "dark-theme": theme === "dark" || (!isFullWidthContent && (isCentered || (isMobile && smallHeightOnMobile))),
    "light-theme": theme === "light",
  });
  const fullWidthImageClasses = classNames(styles.fullWidthImage, {
    [styles.fullWidthImageCentered]: isCentered,
    [styles.imageContain]: imageFit === "contain",
  });

  return (
    <div
      data-testid="pushBlock"
      className={classNames(className, {
        [styles.smallHeightOnMobile]: smallHeightOnMobile,
      })}
    >
      <Row className={containerClasses} dataTestId="pushBlockRow">
        {shouldShowFullWidthImage && (
          <Column xs={12} md={12} className={styles.fullWidthImageWrapper}>
            <ImageBehaviour
              data-testid="pushBlockFullWidthImage"
              src={imageSrc}
              alt={alt}
              className={fullWidthImageClasses}
            />
          </Column>
        )}
        <Column
          xs={12}
          md={columnMdSize}
          className={classNames(styles.textColumn, {
            [styles.textOrder]: mobileOrientation === "bottom-text",
            [styles.carousel]: mobileOrientation === "bottom-text" && isCarousel,
          })}
        >
          {renderContent()}
        </Column>
        {!shouldShowFullWidthImage && (
          <Column
            xs={12}
            md={isCarousel ? 12 : ["left-side-text-wide", "right-side-text-wide"].includes(orientation) ? 6 : 8}
            className={classNames(styles.imageColumn, {
              [styles.hideOnMobile]: smallHeightOnMobile,
              [styles.imageOrder]: mobileOrientation === "bottom-text",
              [styles.carousel]: mobileOrientation === "bottom-text" && isCarousel,
            })}
          >
            <div
              className={classNames(styles.imageSection, {
                [styles.imageContain]: imageFit === "contain",
              })}
              data-testid={"pushblockImageWrapper"}
            >
              {renderImage ? (
                renderImage({ imgDesktop, imgMobile, alt })
              ) : (
                <ImageBehaviour
                  data-testid="pushBlockImage"
                  src={imageSrc}
                  alt=""
                  className={classNames(styles.image, {
                    [styles.imageContain]: imageFit === "contain",
                  })}
                />
              )}
            </div>
          </Column>
        )}
      </Row>
    </div>
  );
};
